// Voix enregistrée (lot 6) : calage de l'horodatage des mots (Whisper) sur
// les phrases de la narration, silences rognés au début et à la fin, écart
// entre ce qui est dit et le texte, et pose de la voix dans le brouillon.
// Pur (aucun accès réseau ni navigateur).

import { bookendNarrations } from './bookends';
import { roleLabel } from './cast';
import { JRAF_LIMITS as L } from './limits';
import { estimateSentenceTimings, splitSentences, spokenText } from './narration';
import type { CastRoleId, JrafDocument, JrafNarration } from './schema/document';

export interface WordCue {
  w: string;
  startMs: number;
  endMs: number;
}

/** Une voix enregistrée telle que la lit le lecteur. */
export interface VoiceAsset {
  durationMs: number;
  words?: readonly WordCue[] | undefined;
}

/** Marge gardée avant le premier mot (attaque de la voix). */
export const VOICE_LEAD_MS = 120;
/** Marge gardée après le dernier mot (fin de la voix, souffle). */
export const VOICE_TAIL_MS = 350;
/** Une phrase commence au plus tôt ce délai avant son premier mot (dans le silence). */
export const VOICE_SENTENCE_PREROLL_MS = 250;
/** En dessous de cette part de mots retrouvés, la voix ne dit pas le texte. */
export const VOICE_MATCH_MIN = 0.5;
/** En dessous, le calage phrase par phrase n'est pas fiable : on répartit au prorata. */
const ALIGN_MIN = 0.35;

// ------------------------------------------------------------- mots

const UNITS = [
  'zero',
  'un',
  'deux',
  'trois',
  'quatre',
  'cinq',
  'six',
  'sept',
  'huit',
  'neuf',
  'dix',
  'onze',
  'douze',
  'treize',
  'quatorze',
  'quinze',
  'seize',
];
const TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

/** Nombre entier (0 à 100) en mots, comme on le dit (« soixante et onze »). */
export function frenchNumberWords(n: number): string[] {
  if (!Number.isInteger(n) || n < 0 || n > 100) return [String(n)];
  if (n === 100) return ['cent'];
  if (n <= 16) return [UNITS[n]!];
  if (n < 20) return ['dix', UNITS[n - 10]!];
  if (n >= 80) {
    const r = n - 80;
    return r === 0 ? ['quatre', 'vingts'] : ['quatre', 'vingt', ...frenchNumberWords(r)];
  }
  const ten = n >= 70 ? 6 : Math.floor(n / 10);
  const r = n - ten * 10;
  if (r === 0) return [TENS[ten]!];
  if (r === 1 || (n === 71 && r === 11)) return [TENS[ten]!, 'et', ...frenchNumberWords(r)];
  return [TENS[ten]!, ...frenchNumberWords(r)];
}

/** Forme comparable d'un mot : minuscules, sans accents ni ponctuation. */
export function normalizeWord(w: string): string {
  return w
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/æ/g, 'ae')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Mots comparables d'un texte dit : les nombres en lettres (« 36 » → trente
 * six), les sigles de points épelés (« BC » → b c), le reste normalisé.
 */
export function speechTokens(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.match(/[\p{L}\p{N}]+/gu) ?? []) {
    if (/^\d+$/.test(raw)) {
      const n = Number(raw);
      out.push(...(n <= 100 ? frenchNumberWords(n) : [raw]));
    } else if (/^\p{Lu}{2,3}$/u.test(raw)) {
      for (const c of raw) out.push(normalizeWord(c));
    } else {
      const w = normalizeWord(raw);
      if (w) out.push(w);
    }
  }
  return out;
}

/** Deux mots dits sont-ils le même mot (au pluriel ou à une terminaison près) ? */
function sameWord(a: string, b: string): boolean {
  if (a === b) return true;
  const min = Math.min(a.length, b.length);
  if (min < 4) return false;
  let p = 0;
  while (p < min && a[p] === b[p]) p++;
  return p >= Math.max(4, Math.ceil(min * 0.75));
}

/** Plus longue suite commune : pour chaque mot attendu, l'index du mot entendu (-1 : absent). */
function alignTokens(expected: readonly string[], heard: readonly string[]): number[] {
  const n = expected.length;
  const m = heard.length;
  const match = new Array<number>(n).fill(-1);
  if (n === 0 || m === 0) return match;
  const w = m + 1;
  const dp = new Uint16Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i * w + j] = sameWord(expected[i]!, heard[j]!)
        ? dp[(i + 1) * w + j + 1]! + 1
        : Math.max(dp[(i + 1) * w + j]!, dp[i * w + j + 1]!);
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (sameWord(expected[i]!, heard[j]!) && dp[i * w + j] === dp[(i + 1) * w + j + 1]! + 1) {
      match[i] = j;
      i++;
      j++;
    } else if (dp[(i + 1) * w + j]! >= dp[i * w + j + 1]!) i++;
    else j++;
  }
  return match;
}

// --------------------------------------------------------- calage

export interface VoiceAlignment {
  /** début utile dans le fichier (silence du début rogné) */
  offsetMs: number;
  /** début de chaque phrase, depuis `offsetMs` */
  startsMs: number[];
  /** durée utile (silence de la fin rogné) */
  totalMs: number;
  /** part des mots du texte retrouvés dans la voix (0 … 1) */
  matched: number;
}

/**
 * Cale les phrases dites sur l'horodatage des mots. Chaque phrase commence
 * dans le silence qui précède son premier mot ; une phrase dont aucun mot
 * n'est reconnu, ou une voix qui ne dit pas le texte, est répartie au prorata
 * de la longueur des phrases.
 */
export function alignVoice(sentences: readonly string[], voice: VoiceAsset): VoiceAlignment | null {
  const words = (voice.words ?? []).filter((w) => w.endMs >= w.startMs);
  if (words.length === 0 || sentences.length === 0) return null;
  const first = words[0]!;
  const last = words[words.length - 1]!;
  const offsetMs = Math.max(0, first.startMs - VOICE_LEAD_MS);
  const endMs = Math.min(voice.durationMs, Math.max(last.endMs, first.startMs) + VOICE_TAIL_MS);
  const totalMs = Math.max(1, endMs - offsetMs);

  // mots attendus (par phrase) et entendus (avec le mot horodaté d'origine)
  const expected: string[] = [];
  const sentenceOf: number[] = [];
  sentences.forEach((s, i) => {
    for (const t of speechTokens(s)) {
      expected.push(t);
      sentenceOf.push(i);
    }
  });
  const heard: string[] = [];
  const wordOf: number[] = [];
  words.forEach((w, wi) => {
    for (const t of speechTokens(w.w)) {
      heard.push(t);
      wordOf.push(wi);
    }
  });
  const match = alignTokens(expected, heard);
  const found = match.filter((j) => j >= 0).length;
  const matched = expected.length ? found / expected.length : 0;

  // au prorata de la longueur des phrases, entre le premier et le dernier mot
  const proportional = (): number[] => {
    const lengths = sentences.map((s) => Math.max(1, s.length));
    const sum = lengths.reduce((a, b) => a + b, 0);
    const span = Math.max(0, last.endMs - first.startMs);
    let acc = 0;
    return lengths.map((len) => {
      const at = first.startMs + (acc / sum) * span;
      acc += len;
      return Math.round(at);
    });
  };

  let fileStarts: number[];
  if (matched < ALIGN_MIN || sentences.length === 1) fileStarts = proportional();
  else {
    const firstWord = new Array<number | null>(sentences.length).fill(null);
    const lastWord = new Array<number | null>(sentences.length).fill(null);
    match.forEach((j, i) => {
      if (j < 0) return;
      const s = sentenceOf[i]!;
      const wi = wordOf[j]!;
      if (firstWord[s] === null) firstWord[s] = wi;
      lastWord[s] = wi;
    });
    const fallback = proportional();
    fileStarts = sentences.map((_, s) => {
      if (s === 0) return first.startMs;
      // premier mot de la phrase : celui qui suit le dernier mot reconnu de la précédente
      let prev: number | null = null;
      for (let k = s - 1; k >= 0 && prev === null; k--) prev = lastWord[k] ?? null;
      const own = firstWord[s] ?? null;
      const after = prev !== null ? prev + 1 : null;
      const wi = own !== null ? (after !== null ? Math.min(after, own) : own) : after;
      if (wi === null || wi >= words.length) return fallback[s]!;
      const start = words[wi]!.startMs;
      const prevEnd = wi > 0 ? words[wi - 1]!.endMs : first.startMs;
      return Math.round(
        start - Math.min(VOICE_SENTENCE_PREROLL_MS, Math.max(0, start - prevEnd) / 2),
      );
    });
    // toujours dans l'ordre : sinon, au prorata
    for (let s = 1; s < fileStarts.length; s++)
      if (fileStarts[s]! <= fileStarts[s - 1]!) {
        fileStarts = fallback;
        break;
      }
  }
  const startsMs = fileStarts.map((t, i) =>
    i === 0 ? 0 : Math.min(totalMs - 1, Math.max(1, Math.round(t - offsetMs))),
  );
  return { offsetMs, startsMs, totalMs, matched };
}

export interface NarrationTimings {
  startsMs: number[];
  totalMs: number;
  /** début de la voix dans son fichier (0 : voix du navigateur, ou voix sans horodatage) */
  offsetMs: number;
}

/**
 * Débuts des phrases et durée d'une narration : estimés (voix du navigateur),
 * calés sur les mots (voix enregistrée horodatée), ou au prorata de la durée
 * du fichier (voix enregistrée sans horodatage).
 */
export function narrationTimings(
  narration: Pick<JrafNarration, 'text' | 'say'>,
  voice: VoiceAsset | null | undefined,
  rate = 1,
): NarrationTimings {
  const est = estimateSentenceTimings(narration, rate);
  if (!voice) return { ...est, offsetMs: 0 };
  const aligned = alignVoice(splitSentences(spokenText(narration)), voice);
  if (aligned)
    return { startsMs: aligned.startsMs, totalMs: aligned.totalMs, offsetMs: aligned.offsetMs };
  if (est.totalMs <= 0) return { ...est, offsetMs: 0 };
  const k = voice.durationMs / est.totalMs;
  return {
    startsMs: est.startsMs.map((s) => Math.round(s * k)),
    totalMs: voice.durationMs,
    offsetMs: 0,
  };
}

/** Part des mots du texte que dit la voix (null : pas d'horodatage). */
export function voiceMatch(
  narration: Pick<JrafNarration, 'text' | 'say'>,
  voice: VoiceAsset,
): number | null {
  return alignVoice(splitSentences(spokenText(narration)), voice)?.matched ?? null;
}

// ----------------------------------------------------- dans le brouillon

export type VoiceTarget =
  | { kind: 'scene'; sceneId: string }
  | { kind: 'hook' }
  | { kind: 'intro' }
  | { kind: 'outro' };

export interface VoiceSlot {
  target: VoiceTarget;
  /** clé stable (liste, tests) : id de la scène, `hook`, `intro`, `outro` */
  key: string;
  label: string;
  hora: string | null;
  /** sous-titres */
  text: string;
  /** à dire (prompteur) */
  say: string;
  audioAssetId: string | null;
  /** jouée dans la vidéo publiée seulement (accroche, fin de la vidéo) */
  videoOnly: boolean;
  /** scène « À toi » : jouée seulement avec l'accès à la carte */
  interactive: boolean;
  /** rôle qui dit ce texte (Speaker 1, 2, 3) ; intro, accroche, fin : Speaker 1 */
  role: CastRoleId;
  /** nom du rôle (« Le prof », « Narrateur ») */
  roleLabel: string;
}

const BOOKEND_KEY = {
  hook: 'hookAudioAssetId',
  intro: 'introAudioAssetId',
  outro: 'outroAudioAssetId',
} as const;

/** Ce qui peut recevoir une voix : accroche (vidéo), intro, chaque scène, fin de la vidéo. */
export function voiceSlots(doc: JrafDocument): VoiceSlot[] {
  const b = bookendNarrations(doc, 'video');
  const slots: (Omit<VoiceSlot, 'role' | 'roleLabel'> & { sceneRole?: CastRoleId })[] = [];
  if (b.hook)
    slots.push({
      target: { kind: 'hook' },
      key: 'hook',
      label: 'Accroche',
      hora: null,
      ...b.hook,
      audioAssetId: doc.bookends?.hookAudioAssetId ?? null,
      videoOnly: true,
      interactive: false,
    });
  slots.push({
    target: { kind: 'intro' },
    key: 'intro',
    label: 'Intro',
    hora: null,
    ...b.intro,
    audioAssetId: doc.bookends?.introAudioAssetId ?? null,
    videoOnly: false,
    interactive: false,
  });
  for (const s of doc.scenes)
    slots.push({
      sceneRole: s.narration.speaker ?? 's1',
      target: { kind: 'scene', sceneId: s.id },
      key: s.id,
      label: s.title,
      hora: s.hora ?? null,
      text: s.narration.text,
      say: spokenText(s.narration),
      audioAssetId: s.narration.audioAssetId ?? null,
      videoOnly: false,
      interactive: s.interactive,
    });
  slots.push({
    target: { kind: 'outro' },
    key: 'outro',
    label: 'Fin de la vidéo',
    hora: null,
    ...b.outro,
    audioAssetId: doc.bookends?.outroAudioAssetId ?? null,
    videoOnly: true,
    interactive: false,
  });
  return slots.map(({ sceneRole, ...slot }) => {
    const role = sceneRole ?? 's1';
    return { ...slot, role, roleLabel: roleLabel(doc, role) };
  });
}

export interface NewVoice {
  path: string;
  durationMs: number;
  words?: WordCue[] | undefined;
  speaker?: string | undefined;
  /** compte qui a enregistré la voix (avatar animé) ; ignoré pour la synthèse */
  speakerId?: string | undefined;
  /** voix de synthèse (export vidéo) : signalée au générique */
  synthetic?: boolean | undefined;
}

type Json = Record<string, unknown>;

function isRecord(v: unknown): v is Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Identifiant de ressource libre, dérivé de la cible (`voix-hypothese`). */
function freeAssetId(base: string, taken: ReadonlySet<string>): string {
  const clean = `voix-${base}`.slice(0, 40).replace(/-+$/, '');
  if (!taken.has(clean)) return clean;
  for (let k = 2; k < 1000; k++) {
    const suffix = `-${k}`;
    const id = `${clean.slice(0, 40 - suffix.length).replace(/-+$/, '')}${suffix}`;
    if (!taken.has(id)) return id;
  }
  throw new Error('trop de ressources');
}

/** Ressources encore utilisées (voix des scènes et des bouts, images). */
function usedAssetIds(doc: Json): Set<string> {
  const used = new Set<string>();
  for (const s of Array.isArray(doc.scenes) ? doc.scenes : []) {
    const id = isRecord(s) && isRecord(s.narration) ? s.narration.audioAssetId : undefined;
    if (typeof id === 'string') used.add(id);
  }
  if (isRecord(doc.bookends))
    for (const key of Object.values(BOOKEND_KEY)) {
      const id = doc.bookends[key];
      if (typeof id === 'string') used.add(id);
    }
  for (const o of Array.isArray(doc.objects) ? doc.objects : [])
    if (isRecord(o) && o.type === 'image' && typeof o.asset === 'string') used.add(o.asset);
  return used;
}

/**
 * Pose (ou retire, `voice = null`) la voix enregistrée d'une cible dans le
 * brouillon BRUT (tel qu'il est en base, sans valeurs par défaut ajoutées).
 * La ressource remplacée disparaît si plus rien ne l'utilise. Renvoie une
 * copie ; lève une erreur si la cible n'existe pas.
 */
export function withVoice(draft: unknown, target: VoiceTarget, voice: NewVoice | null): Json {
  if (!isRecord(draft)) throw new Error('brouillon illisible');
  const doc = structuredClone(draft);
  const assets: unknown[] = Array.isArray(doc.assets) ? doc.assets : [];
  doc.assets = assets;

  // où vit la référence
  let holder: Json;
  let key: string;
  if (target.kind === 'scene') {
    const scene = (Array.isArray(doc.scenes) ? doc.scenes : []).find(
      (s): s is Json => isRecord(s) && s.id === target.sceneId,
    );
    if (!scene || !isRecord(scene.narration))
      throw new Error(`scène « ${target.sceneId} » introuvable`);
    holder = scene.narration;
    key = 'audioAssetId';
  } else {
    if (!isRecord(doc.bookends)) doc.bookends = {};
    holder = doc.bookends as Json;
    key = BOOKEND_KEY[target.kind];
  }
  const previous = typeof holder[key] === 'string' ? (holder[key] as string) : null;

  if (voice) {
    const taken = new Set(
      assets.flatMap((a) => (isRecord(a) && typeof a.id === 'string' ? [a.id] : [])),
    );
    // la voix remplacée garde son identifiant, sauf si une autre cible l'utilise aussi
    delete holder[key];
    const shared = previous !== null && usedAssetIds(doc).has(previous);
    const base = target.kind === 'scene' ? target.sceneId : target.kind;
    const id = previous !== null && !shared ? previous : freeAssetId(base, taken);
    const asset: Json = {
      id,
      kind: 'audio',
      path: voice.path,
      durationMs: Math.round(voice.durationMs),
      voice: voice.synthetic ? 'synthetic' : 'recorded',
    };
    if (voice.words?.length) asset.words = voice.words.slice(0, L.maxWordCues);
    if (voice.speaker?.trim()) asset.speaker = voice.speaker.trim().slice(0, 60);
    if (voice.speakerId && !voice.synthetic) asset.speakerId = voice.speakerId;
    const at = assets.findIndex((a) => isRecord(a) && a.id === id);
    if (at >= 0) assets[at] = asset;
    else assets.push(asset);
    holder[key] = id;
  } else delete holder[key];

  // l'ancienne voix n'est plus utilisée nulle part : on la retire
  if (previous && holder[key] !== previous && !usedAssetIds(doc).has(previous))
    doc.assets = assets.filter((a) => !(isRecord(a) && a.id === previous));
  if (isRecord(doc.bookends) && Object.keys(doc.bookends).length === 0) delete doc.bookends;
  return doc;
}

/**
 * Nom de qui parle (générique « Voix : … »), posé sur les voix enregistrées.
 * Avec `speakerId` : seulement les voix de ce compte (et celles sans compte) —
 * une animation enregistrée à plusieurs garde le prénom de chacun.
 */
export function withVoiceSpeaker(draft: unknown, speaker: string, speakerId?: string): Json {
  if (!isRecord(draft)) throw new Error('brouillon illisible');
  const doc = structuredClone(draft);
  const name = speaker.trim().slice(0, 60);
  for (const a of Array.isArray(doc.assets) ? doc.assets : [])
    if (
      isRecord(a) &&
      a.kind === 'audio' &&
      a.voice !== 'synthetic' &&
      (!speakerId || typeof a.speakerId !== 'string' || a.speakerId === speakerId)
    ) {
      if (name) a.speaker = name;
      else delete a.speaker;
    }
  return doc;
}

// ---------------------------------------------- correction par l'IA

/**
 * Brouillon sans ses voix enregistrées (ressources audio et références) :
 * ce qu'on montre à l'IA quand elle corrige l'animation (les horodatages
 * alourdiraient la demande ; elle n'a pas à y toucher).
 */
export function withoutVoices(draft: unknown): Json {
  if (!isRecord(draft)) throw new Error('brouillon illisible');
  const doc = structuredClone(draft);
  if (Array.isArray(doc.assets))
    doc.assets = doc.assets.filter((a) => !(isRecord(a) && a.kind === 'audio'));
  for (const s of Array.isArray(doc.scenes) ? doc.scenes : [])
    if (isRecord(s) && isRecord(s.narration)) delete s.narration.audioAssetId;
  if (isRecord(doc.bookends)) {
    for (const key of Object.values(BOOKEND_KEY)) delete doc.bookends[key];
    if (Object.keys(doc.bookends).length === 0) delete doc.bookends;
  }
  return doc;
}

function spokenOf(narration: unknown): string | null {
  if (!isRecord(narration)) return null;
  const said = typeof narration.say === 'string' ? narration.say : narration.text;
  return typeof said === 'string' ? said.replace(/\s+/g, ' ').trim() : null;
}

/**
 * Remet les voix enregistrées de `before` dans `after` (document corrigé par
 * l'IA) là où le texte dit n'a pas changé : même scène (même identifiant),
 * même `say` (ou `text`) ; accroche au même texte ; intro au même titre ; fin
 * de la vidéo du même document. Une scène dont le texte a changé perd sa voix
 * (elle ne dirait plus ses sous-titres) : `dropped` la nomme.
 */
export function keepVoices(
  before: unknown,
  after: unknown,
): { doc: Json; kept: string[]; dropped: string[] } {
  if (!isRecord(before) || !isRecord(after)) throw new Error('brouillon illisible');
  const doc = withoutVoices(after);
  const assets: unknown[] = Array.isArray(doc.assets) ? doc.assets : [];
  doc.assets = assets;
  const oldAssets = new Map(
    (Array.isArray(before.assets) ? before.assets : []).flatMap((a) =>
      isRecord(a) && typeof a.id === 'string' && a.kind === 'audio' ? [[a.id, a] as const] : [],
    ),
  );
  const kept: string[] = [];
  const dropped: string[] = [];
  const restore = (id: unknown): string | null => {
    if (typeof id !== 'string') return null;
    const a = oldAssets.get(id);
    if (!a) return null;
    if (!assets.some((x) => isRecord(x) && x.id === id)) assets.push(structuredClone(a));
    return id;
  };

  const newScenes = new Map(
    (Array.isArray(doc.scenes) ? doc.scenes : []).flatMap((s) =>
      isRecord(s) && typeof s.id === 'string' ? [[s.id, s] as const] : [],
    ),
  );
  for (const s of Array.isArray(before.scenes) ? before.scenes : []) {
    if (!isRecord(s) || typeof s.id !== 'string' || !isRecord(s.narration)) continue;
    const voice = s.narration.audioAssetId;
    if (typeof voice !== 'string') continue;
    const now = newScenes.get(s.id);
    if (
      now &&
      isRecord(now.narration) &&
      spokenOf(now.narration) === spokenOf(s.narration) &&
      // en dialogue, une scène passée à un autre rôle n'a plus la bonne voix
      (now.narration.speaker ?? 's1') === (s.narration.speaker ?? 's1')
    ) {
      const id = restore(voice);
      if (id) {
        now.narration.audioAssetId = id;
        kept.push(s.id);
        continue;
      }
    }
    dropped.push(s.id);
  }

  const b0 = isRecord(before.bookends) ? before.bookends : {};
  const same = {
    hook:
      JSON.stringify(isRecord(b0.hook) ? [b0.hook.text, b0.hook.say] : null) ===
      JSON.stringify(
        isRecord(doc.bookends) && isRecord(doc.bookends.hook)
          ? [doc.bookends.hook.text, doc.bookends.hook.say]
          : null,
      ),
    intro: before.title === doc.title,
    outro: before.id === doc.id,
  };
  for (const kind of ['hook', 'intro', 'outro'] as const) {
    const voice = b0[BOOKEND_KEY[kind]];
    if (typeof voice !== 'string') continue;
    const id = same[kind] ? restore(voice) : null;
    if (id) {
      if (!isRecord(doc.bookends)) doc.bookends = {};
      (doc.bookends as Json)[BOOKEND_KEY[kind]] = id;
      kept.push(kind);
    } else dropped.push(kind);
  }
  return { doc, kept, dropped };
}
